import { useEffect, useRef, useState } from 'react';

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

const problems = [
  {
    icon: 'ri-speed-line',
    title: 'Pomalý a zastaraný web',
    desc: 'Stránka sa načítavala 8+ sekúnd. Google to trestal a zákazníci odchádzali ešte pred zobrazením produktov.',
    color: 'text-red-400',
    bg: 'bg-red-500/10',
  },
  {
    icon: 'ri-smartphone-line',
    title: 'Nulová mobilná optimalizácia',
    desc: '73 % návštevníkov prišlo z mobilu - no web bol nepoužiteľný. Konverzný pomer na mobile: 0,18 %.',
    color: 'text-rose-400',
    bg: 'bg-rose-500/10',
  },
  {
    icon: 'ri-search-line',
    title: 'Neviditeľnosť v Googli',
    desc: 'Žiadna SEO stratégia. Kľúčové výrazy ako "horský bicykel" - stránka na pozícii 47+. Organická návštevnosť: 280 návštev/mes.',
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
  },
  {
    icon: 'ri-shopping-bag-3-line',
    title: 'Vysoká miera opustenia košíka',
    desc: 'Až 91 % zákazníkov opustilo nákup bez zaplatenia. Zbytočne komplikovaný checkout proces s 7 krokmi.',
    color: 'text-orange-400',
    bg: 'bg-orange-500/10',
  },
];

export default function CaseChallenge() {
  const { ref, inView } = useInView();

  return (
    <section className="py-24 bg-zinc-950 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-700/40 to-transparent" />

      <div className="max-w-7xl mx-auto px-6">
        <div ref={ref} className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left - Context */}
          <div className={`transition-all duration-700 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div className="inline-flex items-center gap-2 bg-zinc-800/60 border border-zinc-700/40 rounded-full px-4 py-1.5 mb-6">
              <i className="ri-focus-3-line text-orange-400 text-sm" />
              <span className="text-zinc-300 text-sm font-semibold">Zadanie & Výzva</span>
            </div>

            <h2 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight">
              Dobrý produkt.<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-400">
                Zlý online predaj.
              </span>
            </h2>

            <p className="text-zinc-400 text-lg leading-relaxed mb-8">
              SalenBike predával prémiové bicykle a doplnky - ale 95 % obratu pochádzalo
              z kamennej predajne. Online kanál generoval menej ako{' '}
              <strong className="text-white">€4 200 mesačne</strong>, napriek tomu, že
              sortiment mal potenciál na celorepublikový predaj.
            </p>

            {/* Client brief box */}
            <div className="bg-zinc-900 border border-zinc-800/60 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-file-text-line text-orange-400" />
                <span className="text-white font-bold text-sm">Pôvodný brief klienta</span>
              </div>
              <blockquote className="text-zinc-300 text-sm leading-relaxed italic border-l-2 border-orange-500/50 pl-4">
                „Máme skvelé bicykle, lojálnych zákazníkov a 9 rokov skúseností. Ale náš web
                predáva za nás menej ako jeden predavač. Chceme z e-shopu spraviť druhú
                predajňu - ideálne tú úspešnejšiu."
              </blockquote>
              <div className="mt-4 flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-zinc-800 overflow-hidden flex-shrink-0">
                  <img
                    src="https://readdy.ai/api/search-image?query=middle%20aged%20slovak%20businessman%20sporty%20casual%20look%2C%20clean%20white%20background%20headshot%20portrait%2C%20confident%20friendly%20smile&width=80&height=80&seq=cs-client1&orientation=squarish"
                    alt="Tomáš Selecký"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div>
                  <p className="text-white text-xs font-semibold">Tomáš Selecký</p>
                  <p className="text-zinc-500 text-xs">Majiteľ, SalenBike s.r.o.</p>
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div className="mt-8 flex items-center gap-6 flex-wrap">
              {[
                { label: 'Zahájenie', value: 'Január 2024', icon: 'ri-calendar-event-line' },
                { label: 'Trvanie', value: '12 mesiacov', icon: 'ri-timer-line' },
                { label: 'Odvetvie', value: 'E-commerce / Šport', icon: 'ri-store-2-line' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${item.icon} text-orange-400/70 text-base`} />
                  </div>
                  <div>
                    <p className="text-zinc-600 text-xs">{item.label}</p>
                    <p className="text-zinc-200 text-sm font-semibold">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Problems grid */}
          <div
            className={`transition-all duration-700 delay-200 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}
          >
            <p className="text-zinc-500 text-sm uppercase tracking-widest font-semibold mb-6">
              Identifikované problémy
            </p>
            <div className="flex flex-col gap-4">
              {problems.map((p, i) => (
                <div
                  key={p.title}
                  className={`bg-zinc-900 border border-zinc-800/60 rounded-2xl p-5 flex gap-4 items-start transition-all duration-500`}
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className={`w-10 h-10 ${p.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <i className={`${p.icon} ${p.color} text-lg`} />
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-sm mb-1">{p.title}</h4>
                    <p className="text-zinc-500 text-sm leading-relaxed">{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Before metrics */}
            <div className="mt-6 grid grid-cols-3 gap-3">
              {[
                { label: 'Mesačné tržby', value: '€4 200', note: 'pred spoluprácou' },
                { label: 'Konverzie', value: '0.4%', note: 'priemerný pomer' },
                { label: 'Organika', value: '280/mes', note: 'návštevnosť' },
              ].map((m) => (
                <div key={m.label} className="bg-zinc-900/60 border border-zinc-800/60 rounded-xl p-4 text-center">
                  <p className="text-red-400 font-black text-xl mb-0.5">{m.value}</p>
                  <p className="text-zinc-400 text-xs font-medium leading-tight">{m.label}</p>
                  <p className="text-zinc-600 text-xs mt-0.5">{m.note}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
